import { Component, OnInit, Output, EventEmitter, ViewChild, TemplateRef } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { AlertHelper } from 'src/app/shared/components/helpers/alert.helpers';
import { debounceTime } from 'rxjs/operators';
import { EquipoTecnologico } from 'src/app/shared/models/equipo-tecnologico.interface';
import { EquipoTecnologicoService } from 'src/app/pages/services/equipo-tecnologico.service';
import { EntregaEquipoLinkService } from 'src/app/pages/services/entrega-equipo-link.service';

@Component({
  selector: 'app-agregar-equipos-tecnologicos',
  templateUrl: './agregar-equipos-tecnologicos.component.html',
  styleUrls: ['./agregar-equipos-tecnologicos.component.css']
})
export class AgregarEquiposTecnologicosComponent implements OnInit {

  @Output() onDebounce: EventEmitter<boolean> = new EventEmitter();
  debouncer: Subject<boolean> = new Subject();

  @ViewChild('content') content!: TemplateRef<any>;

  tiposEquipo: string[] = ['COMPUTADOR', 'CELULAR', 'TABLET'];
  estadosEquipo: string[] = ['DISPONIBLE', 'ASIGNADO', 'DE_BAJA', 'PERDIDO', 'ROBADO', 'DANIADO'];

  modoEdicion: boolean = false;

  equipo: EquipoTecnologico = {
    tipo: '',
    marca: '',
    modelo: '',
    chip: '',
    numeroSerie: '',
    estado: 'DISPONIBLE',
    accesorios: '',
    observacion: '',
    appleIdCorreo: '',
    appleIdContrasena: '',
    numeroRecuperacion: '',
    duenoNumeroRecuperacion: ''
  };

  equiposSimilaresEncontrados: any[] = [];

  // La contraseña se guarda tal cual (no cifrada, decisión explícita), pero se oculta
  // en el formulario por defecto; el ojito la muestra en texto plano.
  mostrarContrasenaAppleId: boolean = false;

  constructor(
    private modalService: NgbModal,
    private alert: AlertHelper,
    private equipoTecnologicoService: EquipoTecnologicoService,
    private entregaEquipoLinkService: EntregaEquipoLinkService
  ) { }

  onNumeroSerieBlur() {
    const valor = this.equipo.numeroSerie?.trim();
    if (this.modoEdicion || !valor || valor.length < 2) {
      this.equiposSimilaresEncontrados = [];
      return;
    }
    this.entregaEquipoLinkService.buscarEquiposSimilares(valor).subscribe(res => {
      this.equiposSimilaresEncontrados = res.data || [];
    });
  }

  usarEquipoSimilar(equipoSimilar: any) {
    this.equipo.numeroSerie = equipoSimilar.numeroSerie;
    this.equiposSimilaresEncontrados = [];
  }

  ngOnInit(): void {

    this.debouncer
      .pipe(debounceTime(500))
      .subscribe(valor => {
        this.onDebounce.emit(valor);
      });
  }

  //Abre el modal en modo "agregar" (equipo nuevo, estado DISPONIBLE por defecto)
  open() {
    this.modoEdicion = false;
    this.limpiar();
    this.modalService.open(this.content, { size: 'lg' });
  }

  //Abre el modal en modo "editar", precargado con los datos del equipo seleccionado.
  //Es invocado desde app-lista-equipos-tecnologicos vía referencia de plantilla (#agregarComp).
  abrirEdicion(equipoSeleccionado: EquipoTecnologico) {
    this.modoEdicion = true;
    this.equipo = {
      id: equipoSeleccionado.id,
      tipo: equipoSeleccionado.tipo,
      marca: equipoSeleccionado.marca,
      modelo: equipoSeleccionado.modelo,
      chip: equipoSeleccionado.chip || '',
      numeroSerie: equipoSeleccionado.numeroSerie,
      estado: equipoSeleccionado.estado || 'DISPONIBLE',
      accesorios: equipoSeleccionado.accesorios || '',
      observacion: equipoSeleccionado.observacion || '',
      appleIdCorreo: equipoSeleccionado.appleIdCorreo || '',
      appleIdContrasena: equipoSeleccionado.appleIdContrasena || '',
      numeroRecuperacion: equipoSeleccionado.numeroRecuperacion || '',
      duenoNumeroRecuperacion: equipoSeleccionado.duenoNumeroRecuperacion || ''
    };
    this.mostrarContrasenaAppleId = false;
    this.modalService.open(this.content, { size: 'lg' });
  }

  limpiar() {
    this.equipo = {
      tipo: '',
      marca: '',
      modelo: '',
      chip: '',
      numeroSerie: '',
      estado: 'DISPONIBLE',
      accesorios: '',
      observacion: '',
      appleIdCorreo: '',
      appleIdContrasena: '',
      numeroRecuperacion: '',
      duenoNumeroRecuperacion: ''
    };
    this.equiposSimilaresEncontrados = [];
    this.mostrarContrasenaAppleId = false;
  }

  validarEquipo() {
    if (
      !this.equipo.tipo ||
      this.equipo.marca.trim().length < 2 || this.equipo.marca.trim().length > 100 ||
      this.equipo.modelo.trim().length < 1 || this.equipo.modelo.trim().length > 100 ||
      this.equipo.numeroSerie.trim().length < 1 || this.equipo.numeroSerie.trim().length > 100
    ) {
      return false;
    }
    else {
      return true;
    }
  }

  //Opciones de marca disponibles según el tipo de equipo seleccionado
  get marcasDisponibles(): string[] {
    if (this.equipo.tipo === 'COMPUTADOR') {
      return ['MacBook', 'Windows'];
    } else if (this.equipo.tipo === 'CELULAR' || this.equipo.tipo === 'TABLET') {
      return ['iOS', 'Android'];
    }
    return [];
  }

  //El campo chip solo se habilita cuando la marca seleccionada es MacBook
  get chipHabilitado(): boolean {
    return this.equipo.marca === 'MacBook';
  }

  //Los datos de Apple ID solo aplican a equipos Apple (MacBook o iOS)
  get appleIdHabilitado(): boolean {
    return this.equipo.marca === 'MacBook' || this.equipo.marca === 'iOS';
  }

  //Si el tipo cambia y la marca actual ya no es válida para el nuevo tipo, se limpia marca y chip
  onTipoChange() {
    if (!this.marcasDisponibles.includes(this.equipo.marca)) {
      this.equipo.marca = '';
      this.equipo.chip = '';
    }
  }

  //Si la marca cambia a algo distinto de MacBook, se limpia el chip
  onMarcaChange() {
    if (!this.chipHabilitado) {
      this.equipo.chip = '';
    }
    if (!this.appleIdHabilitado) {
      this.equipo.appleIdCorreo = '';
      this.equipo.appleIdContrasena = '';
      this.equipo.numeroRecuperacion = '';
      this.equipo.duenoNumeroRecuperacion = '';
    }
  }

  guardar() {
    if (this.validarEquipo()) {
      if (this.modoEdicion) {
        this.actualizar();
      } else {
        this.crear();
      }
    }
    else {
      this.alert.errorAlert('Existen campos vacíos o con un formato no válido, favor revisar');
    }
  }

  private crear() {
    // Antes de crear, valida que no exista otro equipo con el mismo numeroSerie
    this.equipoTecnologicoService.buscarEquipoPorSerie(this.equipo.numeroSerie.trim()).subscribe((res: any) => {
      if (res.data) {
        this.alert.error_small('Ya existe un equipo registrado con ese número de serie');
        return;
      }

      this.equipoTecnologicoService.crearEquipo(this.equipo).subscribe((res: any) => {
        if (res.success == true) {
          this.alert.success_small(res.msg!)
          this.modalService.dismissAll();
          this.limpiar();
          this.debouncer.next(true);
        } else {
          this.alert.error_small(res.msg!);
        }
      })
    });
  }

  private actualizar() {
    this.equipoTecnologicoService.actualizarEquipo(this.equipo).subscribe((res: any) => {
      if (res.success == true) {
        this.alert.success_small(res.msg!)
        this.modalService.dismissAll();
        this.limpiar();
        this.debouncer.next(true);
      } else {
        this.alert.error_small(res.msg!);
      }
    })
  }

}
